import AnalysisLensHelperText from '@components/common/AnalysisLensHelperText'; // Added import
import { IncludedTranscripts } from '@components/interview/analysis-components/IncludeTranscripts';
import { Metadata } from '@components/interview/analysis-components/MetaData';
import { Summary } from '@components/interview/analysis-components/Summary';
import { getQuoteText } from '@utils/representativeQuotes';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HighlightIcon from '@mui/icons-material/Highlight'; // Signal
import PsychologyIcon from '@mui/icons-material/Psychology'; // Main Icon
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver'; // Position
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt'; // Recommendations
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { AnalysisLayout } from './layout';

// Helper to safely parse JSON results
const safeParseResults = (results) => {
  if (!results) return { data: null, error: 'Results data is missing.' };
  try {
    const parsedData = typeof results === 'string' ? JSON.parse(results) : results;
    return { data: parsedData, error: null };
  } catch (error) {
    console.error('Error parsing ReflexivityTrackerAnalysis results:', error);
    return { data: null, error: `Failed to parse results: ${error.message}` };
  }
};

const ReflexivityTrackerAnalysis = ({ item }) => {
  if (!item) {
    return <Alert severity="warning">Analysis item data is missing.</Alert>;
  }

  const { analysis_lens_type, results: rawResults } = item;
  const { data: results, error: parseError } = safeParseResults(rawResults);

  if (parseError) {
    return <Alert severity="error">{parseError}</Alert>;
  }

  if (!results) {
    return <Alert severity="warning">Parsed analysis results are empty.</Alert>;
  }

  // Destructure safely with defaults
  const {
    transcripts_analyzed = 0,
    total_signals = 0,
    position_prevalence = [],
    notable_signals = [],
    priority_recommendations = [],
  } = results;

  const renderListItems = (items, icon = null, stripDash = false) => {
    if (!items || items.length === 0) return null;
    return (
      <List dense disablePadding sx={{ pl: 2, mt: 1, borderLeft: '2px solid #eee' }}>
        {items.map((text, index) => (
          <ListItem key={index} disableGutters sx={{ pt: 0, pb: 0.5 }}>
            {icon && <ListItemIcon sx={{ minWidth: 'auto', mr: 1 }}>{icon}</ListItemIcon>}
            <ListItemText primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}>
              {stripDash && text.startsWith('- ') ? text.substring(2) : text}
            </ListItemText>
          </ListItem>
        ))}
      </List>
    );
  };

  const renderQuotes = (quotes) => {
    if (!quotes || quotes.length === 0) return null;
    return (
      <Box sx={{ mt: 1.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
          Representative Quotes:
        </Typography>
        <List dense disablePadding sx={{ pl: 2, borderLeft: '2px solid #ccc' }}>
          {quotes.map((quote, index) => (
            <ListItem key={index} disableGutters sx={{ pt: 0, pb: 0.5 }}>
              <ListItemText
                primaryTypographyProps={{
                  variant: 'caption',
                  fontStyle: 'italic',
                  color: 'text.secondary',
                }}
              >
                {getQuoteText(quote)}
              </ListItemText>
            </ListItem>
          ))}
        </List>
      </Box>
    );
  };

  return (
    <AnalysisLayout results={results} item={{ ...item, name: 'Reflexivity Tracker Analysis' }}>
      <CardHeader
        title={
          <Typography
            variant="h5"
            gutterBottom
            component="span"
            sx={{ display: 'inline-flex', alignItems: 'center' }}
          >
            <PsychologyIcon sx={{ verticalAlign: 'bottom', mr: 1 }} />
            Reflexivity Tracker Analysis
            {analysis_lens_type && (
              <AnalysisLensHelperText lensName={analysis_lens_type} level="run" size="small" />
            )}
          </Typography>
        }
      />
      <Divider />
      <CardContent>
        <Metadata item={item} />
        <Summary item={item} results={results} />

        {/* Counts Section */}
        <Grid container spacing={2} sx={{ mb: 3, textAlign: 'center' }}>
          <Grid item xs={6}>
            <Typography variant="h4">{transcripts_analyzed}</Typography>
            <Typography variant="caption">Transcripts Analyzed</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="h4">{total_signals}</Typography>
            <Typography variant="caption">Total Signals Identified</Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Position Prevalence Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Researcher Position Prevalence ({position_prevalence.length})
          </Typography>
          {position_prevalence.length > 0 ? (
            position_prevalence.map((pos, index) => (
              <Accordion
                variant="outlined"
                key={`pos-${index}`}
                TransitionProps={{ unmountOnExit: true }}
                sx={{ mb: 1 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <ListItemIcon sx={{ minWidth: 'auto', mr: 1.5 }}>
                      <RecordVoiceOverIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <Typography sx={{ flexGrow: 1, mr: 1, fontWeight: 'medium' }}>
                      {pos.researcher_position || 'Unnamed Position'}
                    </Typography>
                    <Chip label={`Frequency: ${pos.frequency || 0}`} size="small" />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="subtitle2" gutterBottom>
                    Common Effects:
                  </Typography>
                  {renderListItems(pos.common_effects, null, true)}
                  {renderQuotes(pos.representative_quotes)}
                </AccordionDetails>
              </Accordion>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No researcher position prevalence data found.
            </Typography>
          )}
        </Box>

        {/* Notable Signals Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Notable Signals ({notable_signals.length})
          </Typography>
          {notable_signals.length > 0 ? (
            notable_signals.map((signal, index) => (
              <Accordion
                variant="outlined"
                key={`sig-${index}`}
                TransitionProps={{ unmountOnExit: true }}
                sx={{ mb: 1 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <ListItemIcon sx={{ minWidth: 'auto', mr: 1.5 }}>
                    <HighlightIcon fontSize="small" color="secondary" />
                  </ListItemIcon>
                  <Typography sx={{ flexGrow: 1 }}>{signal.moment || 'Unnamed Moment'}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="subtitle2">Researcher Position:</Typography>
                  <Typography variant="body2" paragraph color="text.secondary">
                    {signal.researcher_position || 'N/A'}
                  </Typography>

                  <Typography variant="subtitle2">Effect on Data:</Typography>
                  <Typography variant="body2" paragraph color="text.secondary">
                    {signal.effect_on_data || 'N/A'}
                  </Typography>

                  {renderQuotes(signal.quotes)}
                </AccordionDetails>
              </Accordion>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No notable signals identified.
            </Typography>
          )}
        </Box>

        {/* Priority Recommendations Section */}
        <Box>
          <Typography variant="h6" gutterBottom>
            <ThumbUpAltIcon sx={{ verticalAlign: 'bottom', mr: 1, color: 'success.main' }} />
            Priority Recommendations
          </Typography>
          {priority_recommendations.length > 0 ? (
            renderListItems(
              priority_recommendations,
              <ThumbUpAltIcon fontSize="small" color="success" />,
              true,
            )
          ) : (
            <Typography variant="body2" color="text.secondary">
              No priority recommendations identified.
            </Typography>
          )}
        </Box>
        <IncludedTranscripts item={item} results={results} />
      </CardContent>
    </AnalysisLayout>
  );
};

ReflexivityTrackerAnalysis.propTypes = {
  /** The analysis item object containing results and metadata */
  item: PropTypes.shape({
    _id: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    study_id: PropTypes.string,
    created_at: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    analysis_lens_type: PropTypes.string,
    results: PropTypes.oneOfType([PropTypes.string, PropTypes.object]), // Can be stringified JSON or parsed object
  }),
};

export default ReflexivityTrackerAnalysis;
